import { useTranslation } from 'react-i18next'
import { IconArrowLeft } from '@tabler/icons-react'
import { QuizProgress } from './QuizProgress'
import { SingleSelectQuestion } from './SingleSelectQuestion'
import { MultiSelectQuestion } from './MultiSelectQuestion'
import { YesNoQuestion } from './YesNoQuestion'
import { ScaleRatingQuestion } from './ScaleRatingQuestion'
import { SliderQuestion } from './SliderQuestion'
import { ShowcasePage } from './ShowcasePage'
import { TransitionPage } from './TransitionPage'
import { SocialProofPage } from './SocialProofPage'
import type { QuizPage } from '@/content/quiz/types'
import { cn } from '@/lib/utils'

interface QuizLayoutProps {
  pages: QuizPage[]
  currentIndex: number
  answers: Record<string, string | string[] | number>
  onAnswerChange: (pageId: string, value: string | string[] | number) => void
  onNext: (value?: string | string[] | number) => void
  onBack: () => void
}

export function QuizLayout({
  pages,
  currentIndex,
  answers,
  onAnswerChange,
  onNext,
  onBack,
}: QuizLayoutProps) {
  const { t } = useTranslation('quiz-ui')
  const page = pages[currentIndex]
  if (!page) return null

  const isTransition = page.type === 'transition'
  const isFirstPage = currentIndex === 0
  const isLastPage = currentIndex === pages.length - 1

  const canProceed = () => {
    switch (page.type) {
      case 'single-select':
      case 'yes-no':
        return !!answers[page.id]
      case 'multi-select':
        return Array.isArray(answers[page.id]) && (answers[page.id] as string[]).length >= (page.minSelect ?? 0)
      case 'scale-rating':
      case 'slider':
        return answers[page.id] !== undefined
      case 'showcase':
      case 'social-proof':
        return true
      default:
        return true
    }
  }

  const showNextButton = !isTransition

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="min-h-screen flex flex-col items-center justify-start lg:py-12">
        {/* Back button - top left */}
        <div className="fixed top-4 left-6 lg:left-8 z-20">
          {!isFirstPage && (
            <button
              type="button"
              onClick={onBack}
              className="px-0 py-0 h-8 text-slate-500 rounded-xl text-sm hover:text-slate-700 flex items-center gap-1 group transition-all duration-300 shrink-0"
            >
              <IconArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-all duration-300" />
              {t('back')}
            </button>
          )}
        </div>

        <div className="w-full flex-1 flex items-center lg:max-w-md lg:bg-white lg:rounded-2xl lg:shadow-xl lg:min-h-[500px] px-6 pt-20 pb-40 lg:pt-14 lg:pb-8 lg:px-8">
          {/* <QuizProgress current={currentIndex} total={pages.length} className="mb-8" /> */}

          <div className="flex-1">
            {page.type === 'single-select' && (
              <SingleSelectQuestion
                page={page}
                value={answers[page.id] as string | undefined}
                onSelect={(v) => onAnswerChange(page.id, v)}
              />
            )}
            {page.type === 'multi-select' && (
              <MultiSelectQuestion
                page={page}
                value={answers[page.id] as string[] | undefined}
                onSelect={(v) => onAnswerChange(page.id, v)}
              />
            )}
            {page.type === 'yes-no' && (
              <YesNoQuestion
                page={page}
                value={answers[page.id] as string | undefined}
                onSelect={(v) => onAnswerChange(page.id, v)}
              />
            )}
            {page.type === 'scale-rating' && (
              <ScaleRatingQuestion
                page={page}
                value={answers[page.id] as number | undefined}
                onSelect={(v) => onAnswerChange(page.id, v)}
              />
            )}
            {page.type === 'slider' && (
              <SliderQuestion
                page={page}
                value={answers[page.id] as number | undefined}
                onSelect={(v) => onAnswerChange(page.id, v)}
              />
            )}
            {page.type === 'showcase' && (
              <ShowcasePage page={page} onContinue={() => onNext()} isLastPage={isLastPage} />
            )}
            {page.type === 'transition' && (
              <TransitionPage page={page} onComplete={() => onNext()} />
            )}
            {page.type === 'social-proof' && (
              <SocialProofPage page={page} onContinue={() => onNext()} />
            )}
          </div>

          {showNextButton && (
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-50/95 backdrop-blur-sm lg:relative lg:bottom-auto lg:left-auto lg:right-auto lg:mt-8 lg:p-0 lg:bg-transparent lg:backdrop-blur-none">
              <button
                type="button"
                onClick={() => {
                  if (['single-select', 'yes-no', 'scale-rating', 'slider'].includes(page.type)) {
                    const v = answers[page.id]
                    if (v !== undefined) onNext(v)
                  } else if (page.type === 'multi-select') {
                    const v = answers[page.id] as string[] | undefined
                    if (v && v.length >= (page.minSelect ?? 0)) onNext(v)
                  } else {
                    onNext()
                  }
                }}
                disabled={
                  ['single-select', 'yes-no', 'multi-select', 'scale-rating', 'slider'].includes(
                    page.type
                  ) && !canProceed()
                }
                className={cn(
                  'w-full py-0 px-6 h-14 rounded-xl font-medium transition-colors border-b-4',
                  'bg-yellow-300 text-slate-800 hover:bg-yellow-400 border-yellow-500',
                  'disabled:bg-slate-200 disabled:text-slate-500 disabled:border-slate-300'
                )}
              >
                {page.ctaText || t('continue')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
